/**
 * Component store — interactive components pushed by the backend mid-call.
 *
 * Chat-mode anchoring (lifted from production): widget RPCs land instantly
 * while the agent's text streams a beat later. In text mode we buffer the
 * widget until the accompanying message lands, then commit it right after so
 * it sorts below. If no message arrives within CHAT_WIDGET_GRACE_MS, flush
 * anyway. Audio mode commits immediately.
 *
 * Also owns the response side: which components still await a user response
 * (and the original config needed to format it), completed flows, and the
 * extension registry that lets client packs handle domain-specific
 * `send_dynamic_data` component types without polluting core.
 */
import { createStore } from "./store.js";
import { CHAT_WIDGET_GRACE_MS, CHAT_WIDGET_AFTER_TEXT_MS } from "../constants.js";
export function createComponentStore(clock, 
/** Commits a component to the timeline — wired to MessageStore + host events by the controller */
commitComponent) {
    let pendingChatWidgets = [];
    let graceTimer = null;
    const pendingResponses = new Map();
    const completedFlowIds = new Set();
    let extensions = [];
    const build = () => ({ completedFlowIds: new Set(completedFlowIds) });
    const handle = createStore(build);
    const store = {
        getSnapshot: handle.getSnapshot,
        subscribe: handle.subscribe,
        dispose: handle.dispose,
        add(component, mode) {
            if (mode !== "text") {
                commitComponent(component);
                return;
            }
            pendingChatWidgets.push(component);
            if (!graceTimer) {
                graceTimer = clock.setTimeout(() => store.flush(), CHAT_WIDGET_GRACE_MS);
            }
        },
        onAgentTextLanded() {
            if (pendingChatWidgets.length > 0) {
                clock.setTimeout(() => store.flush(), CHAT_WIDGET_AFTER_TEXT_MS);
            }
        },
        flush() {
            if (graceTimer) {
                clock.clearTimeout(graceTimer);
                graceTimer = null;
            }
            const queued = pendingChatWidgets;
            pendingChatWidgets = [];
            queued.forEach((component) => commitComponent(component));
        },
        // ─── Response tracking ─────────────────────────────────────────────────────
        trackPendingResponse(componentId, pending) {
            pendingResponses.set(componentId, pending);
        },
        takePendingResponse(componentId) {
            const pending = pendingResponses.get(componentId);
            if (pending)
                pendingResponses.delete(componentId);
            return pending;
        },
        markFlowComplete(componentId) {
            completedFlowIds.add(componentId);
            handle.commit();
        },
        // ─── Extensions ────────────────────────────────────────────────────────────
        registerExtension(extension) {
            extensions.push(extension);
            return () => {
                extensions = extensions.filter((e) => e !== extension);
            };
        },
        runExtensions(componentName, parsed) {
            return extensions.some((extension) => extension(componentName, parsed));
        },
        // ─── Lifecycle ─────────────────────────────────────────────────────────────
        reset() {
            if (graceTimer) {
                clock.clearTimeout(graceTimer);
                graceTimer = null;
            }
            pendingChatWidgets = [];
            pendingResponses.clear();
            completedFlowIds.clear();
            handle.commit();
        },
    };
    return store;
}
